import { TLangs } from '@/plugins/i18n/models'
import { ApiResponse } from '@/api/types'
import { Story } from '@/composables/stories/types'
import type { FullButtonType } from '@/composables/shared/custom-links'

export interface QrMenuHeader {
  id: number
  name: string
  logo: string
  address: string
  lat?: string | number
  lng?: string | number
  background_url: string
  wifi: string
  phone: string
  instagram: string
  facebook?: string
  telegram?: string
  menu_id: string
  restaurant_id?: string
  close: string
  open: string
  avg_bill?: string
  store_id?: number
  avg_cooking?: string
  rating?: number | string
  cashback?: number | string
}

export type QrMenuModifier = {
  id: number
  name: string
  price: number
  up: number
  down: number
  amount?: number
  /** null — бесконечно, 0 — недоступен, >0 — лимит остатка */
  available: number | null
}

/** Доступность одного модификатора в ответе API `/availability` */
export interface ProductAvailabilityModifier {
  /** Числовой id модификатора (совпадает с QrMenuModifier.id) */
  id: number
  /** null — бесконечно, 0 — недоступен, >0 — лимит остатка */
  available: number | null
}

/** Тело `data` в ответе GET `.../availability` */
export interface ProductAvailabilityData {
  /** null — бесконечно, 0 — недоступен, >0 — лимит остатка */
  available: number | null
  modifiers: ProductAvailabilityModifier[]
}

/** Полный ответ GET `.../availability` */
export interface ProductAvailabilityResponse {
  success: boolean
  data: ProductAvailabilityData
}

export type QrMenuProduct = {
  id: string
  product_id: string
  name: string
  description: string
  price: number
  imageUrl: string | null
  bigImageUrl: string | null
  disabled: 0 | 1
  isCombo: 0 | 1
  isSet: 0 | 1
  order: number
  code: string | null
  spic: string | null
  subCategory: string | null
  unit: string
  weight: string
  /** null — бесконечно, 0 — недоступен, >0 — лимит остатка (стоки) */
  available: number | null
  unavailable: boolean
  modifiers: Array<{
    id: number
    title: string
    up: number
    down: number
    items: QrMenuModifier[]
  }>
}

export interface QrMenuCategory {
  id: number
  name: string
  order: number
  parentId?: number
  imageUrl?: string
  items: QrMenuProduct[]
}

export interface QrMenuData {
  id: number
  name: string
  logo: string
  address: string
  lat?: string | number
  lng?: string | number
  background_url: string
  result: QrMenuCategory[]
  stories: Story[]
  color: string
  theme: 'dark' | 'light'
  wifi: string
  phone: string
  instagram: string
  menu_id: string
  store_id: number
  available_locales: AvailableLangs
  close: string
  open: string
  facebook?: string
  restaurant_id: string
  telegram?: string
  avg_bill?: string
  avg_cooking?: string
  rating?: number | string
  cashback?: number | string
}

export type ApiFlag = 0 | 1

export interface TapLinkStoreLink {
  id: number | null
  title: string
  url: string
  icon: string
  type: FullButtonType | string
  hide: ApiFlag
}

export interface TapLinkStoreHall {
  id: number
  store_id: number
  hall_id: string
  name: string
  status: ApiFlag
}

export interface TapLinkStoreMetaItem {
  id: number
  title: string
  pivot?: { store_id: number; filter_id: number }
}

export interface TapLinkStoreBranchAddress {
  name: string
  coordinates: string | null
  distance: string | null
  bg?: string
  logo?: string
  rating?: number
  stories?: Story[]
  attributes?: unknown[]
  brand?: unknown
  address?: string | null
  phone?: string
  telegram?: string
  instagram?: string
}

export interface TapLinkStoreWorkingHours {
  mo?: { open: string; close: string }
  tu?: { open: string; close: string }
  we?: { open: string; close: string }
  th?: { open: string; close: string }
  fr?: { open: string; close: string }
  sa?: { open: string; close: string }
  su?: { open: string; close: string }
}

/** Ответ API /menu/store/:id */
export interface TapLinkStoreResponse {
  id: number
  name: string
  address: string
  logo?: string
  logo_url: string
  background_url: string
  description: string | null
  brand_id: number
  lat: string
  lng: string
  coordinates: string
  stories: Story[]
  links: TapLinkStoreLink[]
  halls: TapLinkStoreHall[]
  classifications: TapLinkStoreMetaItem[]
  cuisines: TapLinkStoreMetaItem[]
  filters: TapLinkStoreMetaItem[] | null
  additionals: TapLinkStoreMetaItem[]
  'branch-address': TapLinkStoreBranchAddress[]
  cashback: number | string
  pay_without_accept: ApiFlag
  auth_required: ApiFlag
  button_color: string
  day_n_night: ApiFlag
  open: string
  close: string
  working_hours: TapLinkStoreWorkingHours
  avg_bill: number | string | null
  avg_cooking: number | string | null
  delivery_time: number | string | null
  delivery_price: number | string | null
  phone: string
  telegram: string
  instagram: string
  facebook: string | null
  theme?: 'dark' | 'light'
  color?: string
  wifi?: string | null
  rating?: number | string | null
}

export interface Pallete {
  theme: 'dark' | 'light'
  bgColor: string
  productCardColor: string
  primaryColor: string
  secondaryColor: string
  borderColor: string
  textColor: string
  secondaryTextColor: string
  priceBlockColor: string
}

export interface CartProduct {
  key: string
  id: string
  count: number
  modifiers: Array<{
    id: number
    amount: number
  }>
}

export interface CartProductFromStorage {
  key: string
  name: string
  imageUrl: string
  price: number
  id: string
  count: number
  modifiers: Array<{
    id: number
    amount: number
    name: string
  }>
}

// Создание заказа (qr-menu)
export type QrMenuOrderType = 'on_table' | 'delivery' | 'takeaway' | 'spot'

export interface QrMenuOrderItemModifier {
  id: string
  name: string
  price: number
  count: number
}

export interface QrMenuOrderItemCategory {
  id: string
}

export interface QrMenuOrderItem {
  id: string
  category: QrMenuOrderItemCategory
  name: string
  description: string
  price: number
  unit: string
  count: number
  imageUrl: string
  bigImageUrl: string
  spic: string
  code: string
  weight: number
  modifiers: QrMenuOrderItemModifier[]
}

/** Элемент списка платёжных систем (`GET /menu/profile/payment-systems`) */
export interface QrMenuPaymentSystem {
  name: string
  disabled: 0 | 1
  hidden: 0 | 1
  label: string | null
  icon: string
  hint: string | null
  order: number
}

export interface QrMenuAddCardResponse {
  card_token: string
  phone: string
  '3ds': {
    acs_url: string | null
    redirect_url: string
    pa_req: string | null
    term_url: string | null
    md: string | null
    version: string | null
    html: string | null
    ds_trans_id: string | null
  }
}

/** Ответ `GET /review/card-info?bin=…` */
export interface QrMenuCardInfoReview {
  bin: string | null
  name: string | null
  color: string | null
  logo: string | null
  ps: string | null
  ps_bank_id: string | number | null
  country: string | null
}

/** Данные карты после успешного `PUT /payment/card/{card_token}` (подтверждение OTP) */
export interface QrMenuConfirmedCard {
  token: string
  pan: string
  ps: string
  holder_name: string
  bank: string
}

export interface QrMenuCreateOrderPayload {
  menu_id: string
  table_id: string | null
  store_id: string | null
  type: QrMenuOrderType
  price: number
  items: QrMenuOrderItem[]
  without_request: boolean
  /** Комментарий к заказу (если бэкенд поддерживает) */
  comment?: string
  payment_system?: string
  /** Оплата сохранённой картой */
  card?: {
    token: string
  }
}

/** Тело POST `/menu/table-order` (QR за столом, query `?t=…`) */
export interface QrMenuTableOrderPayload {
  menu_id: string
  table_id: string
  type: 'on_table'
  price: number
  items: QrMenuOrderItem[]
  comment?: string
  payment_system?: string
  card?: {
    token: string
  }
}

export enum QrMenuCheckDetailsType {
  STATUS_ERROR = -1, // Ошибка при создании заказа
  STATUS_DRAFT = 0, // Заказ не оплачен
  STATUS_NEW = 1, // Заказ создан
  STATUS_APPROVED = 2, // Заказ принят
  STATUS_WAITING = 3, // Принят, но не готовится (ожидает)
  STATUS_PREPARING = 4, // Готовится
  STATUS_READY = 5, // Приготовлен / ждет отправки
  STATUS_SHIPPING = 6, // В пути
  STATUS_DELIVERED = 7, // Завершен (доставлен)
  STATUS_CANCELED = 8, // Отменен
  STATUS_DELETED = 9 // Удалён
}

export interface QrMenuCheckProduct {
  id: string
  name: string
  description: string
  price: number
  image: string
  bigImageUrl: string
  count: number
  modifiers: Array<{
    id: number
    count: number
    name: string
  }>
}

/**
 * Блок `payment` внутри `data` ответа POST /menu/fast-order (Payme и др.).
 * Для оплаты картой: `id` — путь POST `/menu/payment/{id}` с OTP; редирект может отсутствовать.
 */
export interface QrMenuFastOrderPayment {
  /** Идентификатор платежа для POST `/menu/payment/{id}` */
  id?: string | number
  redirect?: string | null
  /** Для polling `/review/status` и т.п.; может совпадать с `id` */
  payment_id?: string | number | null
  amount?: string
  store_id?: number
  order_guid?: string | null
  status?: string
  payment_system?: string
}

/** Позиция в `order.details.items` ответа POST `/menu/fast-order` */
export interface QrMenuFastOrderApiOrderItem {
  id?: string | number
  name?: string
  description?: string | null
  price?: number
  imageUrl?: string | null
  image?: string
  bigImageUrl?: string | null
  count?: number
  modifiers?: Array<{
    id?: string | number
    name?: string
    price?: number
    count?: number
  }>
}

export interface QrMenuFastOrderApiOrderDetails {
  guid?: null
  orderSum?: number
  unpaidSum?: number
  discountSum?: number
  totalPieces?: number
  number?: string | null
  paid?: number
  finished?: number
  status?: number
  id?: string | number | null
  code?: string | null
  items?: QrMenuFastOrderApiOrderItem[]
  table?: { id?: string | null; code?: string | null; name?: string | null }
  cashdesk?: { id?: string | null; code?: string | null; name?: string | null }
}

/** Заведение в ответе GET `/menu/order/{uuid}` (внутри заказа) */
export interface QrMenuOrderApiStore {
  id?: number
  name?: string
  logo?: string
  bg?: string
  brand?: {
    uid?: string
    id?: number
  }
}

/** Заказ в `data.order` POST fast-order или плоский `data` GET `/menu/order/{uuid}` */
/** Элемент массива `response` у заказа при бизнес-ошибке (напр. `status: -1`) */
export interface QrMenuFastOrderApiOrderResponseItem {
  code?: number
  description?: string
}

export interface QrMenuFastOrderApiOrder {
  id: number
  uid: string
  uuid: string
  store_id: number
  number: string
  type: QrMenuOrderType
  paid: boolean
  finished: boolean
  status?: number
  response?: QrMenuFastOrderApiOrderResponseItem[] | null
  order_number?: string | null
  /** Стол: число или строка (GUID), напр. для `service: table-order` */
  table_id?: string | number | null
  total_price?: number
  details?: QrMenuFastOrderApiOrderDetails
  store?: QrMenuOrderApiStore
  menu?: { id?: number; url?: string }
  /** Напр. `fast-order` — возврат в меню по `/m/{brand.uid}?f={store_id}` */
  service?: string
}

/** Тело `data` ответа POST `/menu/fast-order`: вложенный заказ и опционально оплата */
export interface QrMenuFastOrderResponseData {
  order: QrMenuFastOrderApiOrder
  payment?: QrMenuFastOrderPayment | null
}

export interface QrMenuCheckDetails {
  type: QrMenuOrderType
  uniqid: string
  number: string
  order_id: string
  order_number: string | null
  /** UUID заказа в ответе POST fast-order (если приходит отдельно от order_id) */
  uuid?: string | null
  /** Внешняя оплата приходит в `data.payment` */
  payment?: QrMenuFastOrderPayment | null
  details: {
    guid: null
    orderSum: number
    unpaidSum: number
    discountSum: number
    totalPieces: number
    number: string | null
    paid: number
    finished: number
    status: QrMenuCheckDetailsType
    id: string | null
    code: string | null
    items: QrMenuCheckProduct[]
    employee: {
      id: null
      code: null
      name: null
    }
    table: {
      id: string | null
      code: string | null
      name: string | null
    }
    cashdesk: {
      id: string | null
      code: string | null
      name: string | null
    }
  }
}

export type QrMenuCheckDetailsResponse = ApiResponse<QrMenuCheckDetails>

export type QrMenuFastOrderResponse = ApiResponse<QrMenuFastOrderResponseData>

/**
 * Тело `data` ответа GET `/menu/order/{uuid}`:
 * — плоский объект заказа (с `store`, `menu`, …);
 * — или `{ order: { … } }`.
 */
export type QrMenuGetOrderResponseBody =
  | QrMenuFastOrderApiOrder
  | { order: QrMenuFastOrderApiOrder }
export type QrMenuGetOrderByUuidResponse = ApiResponse<QrMenuGetOrderResponseBody>

export interface PaymentReviewPaymentInfo {
  id: string
  payment_id: number
  takeaway: boolean
  table_id: string | null
  delivery: boolean
  order_guid: string | null
  status: string
  store_id: number
  payment_system: string
  amount: string
  datetime: string
  receipt_url: string | null
  tax_receipt_url: string | null
  return_url: string | null
}

export interface PaymentReviewStatusPayload {
  status: boolean
  payment_info: PaymentReviewPaymentInfo
}

export type PaymentReviewStatusResponse = ApiResponse<PaymentReviewStatusPayload>

export interface GetMenuArgs {
  brandId: string
  storeId?: string
  tableId?: string
  delivery?: string
  fast?: string
  locale?: TLangs
}

export type QrmenuCreateResponse = ApiResponse<QrMenuOrderCreatePayload>

export interface QrMenuOrderCreatePayload {
  data: {
    order_id: string
  }
}

export interface AvailableLangs {
  ru: boolean
  en: boolean
  uz: boolean
}
